import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, HeartHandshake, Scale, Megaphone, Sparkles } from "lucide-react";
import PageMeta from "../components/PageMeta";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/4557cc87-798c-47bd-b347-d002eb8baf36/images/7fe925042e96febf2b283cf03b820c66b06875424519f4bb4ff12cccf345b398.png";
const HANDS_IMG =
  "https://static.prod-images.emergentagent.com/jobs/4557cc87-798c-47bd-b347-d002eb8baf36/images/9607f2d9374d64d9ef140b0cd80f9c96c49a7e27998882fda1435ed6b902b2b7.png";

const LAUNCH_DAY = new Date(2026, 9, 1);

const LaunchNote = () =>
  new Date() < LAUNCH_DAY ? (
    <div
      data-testid="launch-note"
      className="mt-8 inline-flex items-start gap-2.5 border border-ember/40 bg-white/70 backdrop-blur px-4 py-3 max-w-xl"
    >
      <Sparkles size={14} strokeWidth={1.5} className="text-ember mt-0.5 shrink-0" aria-hidden />
      <p className="text-sm text-ink-muted leading-relaxed font-accent">
        <strong className="text-ink font-semibold">We launch fully on 1 October 2026.</strong>{" "}
        Everything here already works — feel free to look around while we put the
        finishing touches in place.
      </p>
    </div>
  ) : null;

const Hero = () => (
  <section data-testid="hero-section" className="relative overflow-hidden border-b border-rule">
    <div className="absolute inset-0 z-0">
      <img
        src={HERO_IMG}
        alt=""
        className="w-full h-full object-cover opacity-50"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-linen/40 via-linen/85 to-linen" />
    </div>
    <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-24 pb-20 md:pt-36 md:pb-28">
      <div className="max-w-3xl animate-in">
        <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
          For families in the child protection system
        </div>
        <h1 className="font-serif font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-ink mt-6 text-balance leading-[1.02]">
          You are not the first family to feel this way. <em className="text-river not-italic font-serif italic">You are not alone.</em>
        </h1>
        <p className="mt-8 text-lg md:text-xl text-ink-muted leading-relaxed max-w-2xl font-light">
          A calm, clear starting point for parents, grandparents and kinship carers
          caught up in the child protection system in England and Wales —
          and a quiet, persistent voice for a more open one.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <Link
            to="/understanding"
            data-testid="hero-understand-button"
            className="inline-flex items-center justify-center gap-2 bg-river text-linen px-7 py-4 text-sm font-accent font-semibold tracking-wide hover:bg-river-dark transition-colors"
          >
            Understand the process
            <ArrowUpRight size={16} strokeWidth={1.5} />
          </Link>
          <Link
            to="/support"
            data-testid="hero-support-button"
            className="inline-flex items-center justify-center gap-2 border border-ink/30 text-ink px-7 py-4 text-sm font-accent font-semibold tracking-wide hover:border-ink hover:bg-white transition-colors"
          >
            Find support today
          </Link>
        </div>
        <LaunchNote />
      </div>
    </div>
  </section>
);

const OpeningEssay = () => (
  <section className="bg-linen border-b border-rule">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <aside className="lg:col-span-3">
          <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
            A note to begin
          </div>
          <div className="mt-4 h-px w-12 bg-ember/60" />
        </aside>
        <div className="lg:col-span-9 space-y-6 text-lg md:text-xl text-ink-muted leading-[1.75] font-light">
          <p className="text-2xl md:text-3xl font-serif text-ink leading-snug text-balance">
            If you have found your way to this site, there is a good chance you are frightened, exhausted, and struggling to understand what is happening to your family.
          </p>
          <p>
            Perhaps social workers have become involved in your life. Perhaps you
            are facing a meeting, an assessment, or a hearing in the family court.
            Perhaps your children have already been removed, and you are desperate
            to know how this happened and what you can do.
          </p>
          <p>
            Whatever has brought you here — please know this: you are not the
            first family to feel this way, and you are not alone.
          </p>
          <p>
            This site was created for parents, grandparents, kinship carers, and
            whole families caught up in the child protection system in England
            and Wales. We are here to help you understand the process, find your
            footing, and remember that you still have a voice.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const PILLAR_ITEMS = [
  {
    icon: BookOpen,
    to: "/understanding",
    title: "Understand the system",
    body: "Plain-English explanations of what a child protection conference is, what each court order means, and what to expect at each stage.",
    testid: "pillar-understand",
  },
  {
    icon: HeartHandshake,
    to: "/support",
    title: "Find proper support",
    body: "A curated directory of solicitors, advocates and emotional support — the people who can stand beside you when it matters most.",
    testid: "pillar-support",
  },
  {
    icon: Scale,
    to: "/glossary",
    title: "Decode the language",
    body: "Section 47, ICO, EPO, LADO. The system speaks in initials. We translate them — clearly, without condescension.",
    testid: "pillar-glossary",
  },
  {
    icon: Megaphone,
    to: "/campaign",
    title: "A campaign for openness",
    body: "Why a system that spends £15.5 billion a year, behind closed doors, must be more transparent — and what we are doing about it.",
    testid: "pillar-campaign",
  },
];

const PillarCard = ({ icon: Icon, to, title, body, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="group bg-linen hover:bg-white p-8 md:p-10 transition-colors"
  >
    <Icon size={28} strokeWidth={1.25} className="text-river" />
    <h3 className="font-serif text-2xl md:text-[1.7rem] mt-6 text-ink tracking-tight leading-tight">
      {title}
    </h3>
    <p className="mt-3 text-ink-muted leading-relaxed">{body}</p>
    <div className="mt-6 inline-flex items-center gap-1.5 text-river font-accent text-sm font-semibold group-hover:gap-2.5 transition-all">
      Read more
      <ArrowUpRight size={14} strokeWidth={1.5} />
    </div>
  </Link>
);

const Pillars = () => (
  <section data-testid="pillars-section" className="border-b border-rule">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
            What this site offers
          </div>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mt-4 text-ink leading-[1.05] tracking-tight">
            Four things, done carefully.
          </h2>
        </div>
        <p className="lg:col-span-6 lg:col-start-7 text-lg text-ink-muted leading-relaxed font-light">
          We will never pretend to replace a solicitor or a qualified advocate. What we can do is help you walk into those rooms feeling a little more informed, a little more prepared, and a great deal less alone.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-rule border border-rule">
        {PILLAR_ITEMS.map((item) => (
          <PillarCard key={item.to} {...item} />
        ))}
      </div>
    </div>
  </section>
);

const Quote = () => (
  <section className="bg-linen-alt border-b border-rule">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <img
            src={HANDS_IMG}
            alt="Hands resting together on a table"
            className="w-full h-[460px] object-cover"
          />
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
            What we believe
          </div>
          <blockquote className="mt-6 font-serif text-3xl md:text-4xl lg:text-[2.5rem] text-ink leading-[1.15] tracking-tight text-balance">
            We do not stand against child protection. We stand for a system that is <em className="italic font-serif">open enough to be trusted</em> — one in which families understand what is happening to them, can challenge decisions that are wrong, and are treated with dignity throughout.
          </blockquote>
          <Link
            to="/campaign"
            data-testid="quote-campaign-link"
            className="mt-10 inline-flex items-center gap-2 text-river font-accent text-sm font-semibold border-b border-river pb-1 hover:gap-3 transition-all"
          >
            Read about our campaign
            <ArrowUpRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const Closing = () => (
  <section className="bg-river text-linen">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 md:py-32 text-center">
      <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
        Our hope for you
      </div>
      <h2 className="mt-6 font-serif text-3xl md:text-5xl text-linen leading-[1.1] max-w-3xl mx-auto text-balance">
        Understanding the system is the first step to facing it.
      </h2>
      <p className="mt-8 text-lg md:text-xl text-linen/80 leading-relaxed max-w-2xl mx-auto font-light">
        You deserve to be heard. You deserve to understand. And you deserve
        support that is on your side.
      </p>
      <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
        <Link
          to="/contact"
          data-testid="closing-contact-button"
          className="inline-flex items-center justify-center gap-2 bg-ember text-ink px-8 py-4 text-sm font-accent font-semibold tracking-wide hover:bg-ember/90 transition-colors"
        >
          Get in touch
        </Link>
        <Link
          to="/stories"
          data-testid="closing-stories-button"
          className="inline-flex items-center justify-center gap-2 border border-linen/30 text-linen px-8 py-4 text-sm font-accent font-semibold tracking-wide hover:bg-linen/10 transition-colors"
        >
          Read other families&rsquo; stories
        </Link>
      </div>
    </div>
  </section>
);

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <PageMeta
        title=""
        description="A calm, clear starting point for parents, grandparents and kinship carers caught up in the child protection system in England and Wales — and a quiet, persistent voice for a more open one."
        path="/"
      />
      <Hero />
      <OpeningEssay />
      <Pillars />
      <Quote />
      <Closing />
    </div>
  );
}
